import { Trans, useTranslation } from 'react-i18next';
import { MyLink, MyTooltip, Table } from '@scramble/ui';
import { prettyMoney, roundMoney } from '@scramble/utils';
import { prettyDate } from '@/helpers/utils';
import { DATE_FORMAT_DD_MM_YYYY } from '@/helpers/common';
import { CURRENCY, LOAN_GROUPS_IDS } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import IconInfo from '@scramble/design-system/icons/info-circle.svg?react';
import s from './index.module.scss';

export const TableView = props => {
  const { t } = useTranslation();
  const { allocatedAmount, tab: groupId } = props;
  const repayment = props.data.repayment_schedule[0].principal;

  const calculateA = (allocated, repayment, percent) => {
    let current = allocated;

    return props.data.repayment_schedule.map((i, index) => {
      const item = {
        date: i.date,
        earnings_percent: percent,
        loan_balance: null,
        earnings: null,
        earnings_base: null,
      };

      if (index === 0) {
        item.loan_balance = current * percent;
      } else {
        current -= repayment;
        item.loan_balance = current;
      }

      item.earnings = roundMoney(item.loan_balance * 0.01, 2, 'halfUp');
      item.earnings_base = item.loan_balance * 0.0075;

      return item;
    });
  };

  const calculateB = (allocated, percent) => {
    const lastMonth =
      props.data.repayment_schedule[props.data.repayment_schedule.length - 1];

    return lastMonth
      ? [
          {
            date: lastMonth.date,
            earnings_percent: percent,
            loan_balance: allocatedAmount,
            earnings: roundMoney((allocatedAmount * 9) / 100, 2, 'halfUp'),
            earnings_base: null,
          },
        ]
      : [];
  };

  const data =
    groupId === LOAN_GROUPS_IDS.conservative.id
      ? calculateA(allocatedAmount, repayment, 1)
      : calculateB(allocatedAmount, 9);

  const { total_earnings, total_earnings_base } = data.reduce(
    (acc, item) => {
      acc.total_loan_balance += item.loan_balance;
      acc.total_earnings += item.earnings;
      acc.total_earnings_base += item.earnings_base;
      return acc;
    },
    { total_loan_balance: 0, total_earnings: 0, total_earnings_base: 0 },
  );

  return (
    <div className={s.wrapper}>
      <Table minWidth={688}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t('investing:earnings_schedule.due_date')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('investing:earnings_schedule.loan_balance')}
            </Table.HeaderCell>
            <Table.HeaderCell className={s.row}>
              <span>{t('investing:earnings_schedule.earnings_percent')}</span>
              <MyTooltip
                renderContent={() => (
                  <div className="f-16 f-400" style={{ maxWidth: '350px' }}>
                    {groupId === LOAN_GROUPS_IDS.conservative.id
                      ? t('investing:earnings_schedule.percent_up_to_1')
                      : t('investing:earnings_schedule.percent_9')}
                  </div>
                )}
                renderElement={() => <IconInfo className={s.icon_tooltip} />}
              />
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('investing:earnings_schedule.earnings_currency', {
                currency: CURRENCY,
              })}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {prettyDate(item.date, DATE_FORMAT_DD_MM_YYYY)}
                </span>
              </Table.Cell>
              <Table.Cell>{prettyMoney(item.loan_balance)}</Table.Cell>
              <Table.Cell>
                {groupId === LOAN_GROUPS_IDS.conservative.id
                  ? t('investing:earnings_schedule.up_to_percent', {
                      percent: item.earnings_percent,
                    })
                  : `${item.earnings_percent}%`}
              </Table.Cell>
              <Table.Cell>{prettyMoney(item.earnings)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div className={s.result}>
        <div className="f-500">{t('investing:earnings_schedule.total_earnings')}:</div>
        <div>
          {groupId === LOAN_GROUPS_IDS.conservative.id && (
            <span style={{ color: 'var(--Grey-300, #C5C1C8)' }}>
              {t('investing:earnings_schedule.up_to')}
            </span>
          )}{' '}
          <span className="f-500">
            {prettyMoney(
              groupId === LOAN_GROUPS_IDS.conservative.id
                ? total_earnings_base + props.data.increased_income
                : total_earnings,
              { rounding: 'halfUp' },
            )}
          </span>
        </div>
      </div>

      {groupId === LOAN_GROUPS_IDS.conservative.id && (
        <div className={s.hint}>
          <Trans
            ns="investing"
            i18nKey="earnings_schedule.note_basic_return"
            values={{
              amount: prettyMoney(total_earnings_base, {
                rounding: 'halfUp',
              }),
              additionalAmount: prettyMoney(props.data.increased_income),
            }}
            components={{
              Span: <span className="f-600" />,
              HelpLink: (
                <MyLink
                  href={APP_LINKS.helpLoanTerms()}
                  className="f-600"
                  underline
                />
              ),
            }}
          />
        </div>
      )}
    </div>
  );
};
